<template>
  <div class="product-bundles-content">
    <Loading :loading="loading" loadingText="Loading...">
    <div class="products-content-wrapper" :class="{ 'loading': loading }">
    <!-- Filter Row - Vibrant Products Style -->
    <div class="filter-row">
      <div class="filter-item">
        <label class="filter-label">Sort By</label>
        <SingleSelector
          v-model="sortBy"
          size="medium"
          placeholder="Recommendation"
          :options="sortByOptions"
        />
      </div>
      <div v-if="providerOptions.length > 0" class="filter-item">
        <label class="filter-label">Provider</label>
        <SingleSelector
          v-model="selectedProvider"
          size="medium"
          placeholder="Select"
          :options="providerOptions"
        />
      </div>
      <ResponsiveButton
        class="add-new-bundle-btn-wrapper"
        button_type="filled"
        built_in_theme="dark"
        size="medium"
        display_name="Create New Bundle"
        @click-button="handleCreateBundle"
      >
        <template #prefix>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12.6667 8.66667H8.66671V12.6667H7.33337V8.66667H3.33337V7.33333H7.33337V3.33333H8.66671V7.33333H12.6667V8.66667Z" fill="white"/>
          </svg>
        </template>
      </ResponsiveButton>
    </div>

    <!-- Product Bundles Table -->
    <div class="products-table-wrapper">
      <div class="products-table">
        <!-- Table Header -->
        <div class="table-header">
          <div class="th bundle-name">BUNDLE & DESCRIPTION</div>
          <div class="th price">WHOLESALE PRICE</div>
          <div class="th price">RETAIL PRICE</div>
          <div class="th actions">ACTIONS</div>
        </div>

        <!-- Table Body -->
        <div class="table-body">
          <!-- No data state -->
          <div v-if="displayItems.length === 0" class="no-data-state">
            <p>No results found.</p>
            <p>We couldn't find what you searched for. Try searching again.</p>
          </div>

          <!-- Data rows -->
          <div
            v-for="bundle in displayItems"
            :key="bundle.id"
            class="table-row"
          >
            <div class="td bundle-name">
              <div class="bundle-info">
                <span class="bundle-name-text">{{ bundle.name }}</span>
                <span class="test-count">{{ getTestDescription(bundle) }}</span>
              </div>
            </div>
            <div class="td price">{{ currencySymbol }}{{ bundle.wholeSalePrice.toFixed(2) }}</div>
            <div class="td price">{{ currencySymbol }}{{ bundle.markedUpPrice.toFixed(2) }}</div>
            <div class="td actions" @click.stop>
              <button class="edit-btn" @click.stop="handleEdit(bundle)">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M21.4555 5.41575C21.6477 5.70687 21.6157 6.10248 21.3594 6.35876L12.167 15.5511C12.0728 15.6454 11.9552 15.7128 11.8263 15.7465L7.99783 16.7465C7.87296 16.7791 7.74425 16.7784 7.62332 16.7476C7.49475 16.7149 7.37498 16.6482 7.27797 16.5511C7.08969 16.3629 7.01535 16.0889 7.08264 15.8313L8.08264 12.0028C8.11211 11.89 8.1674 11.7786 8.24389 11.6912L17.4703 2.46967C17.5511 2.38891 17.6484 2.32846 17.7543 2.29163C17.8328 2.26432 17.916 2.25 18.0007 2.25C18.1996 2.25 18.3903 2.32902 18.531 2.46967L21.3594 5.2981C21.396 5.33471 21.4281 5.37416 21.4555 5.41575ZM19.7684 5.82843L18.0007 4.06066L9.48251 12.5788L8.85752 14.9716L11.2503 14.3466L19.7684 5.82843Z" fill="#374957"/>
                  <path d="M19.642 17.1603C19.9154 14.8227 20.0024 12.4688 19.903 10.1208C19.8982 10.0084 19.9405 9.89898 20.0201 9.81942L21.0034 8.83609C21.1243 8.71519 21.3309 8.79194 21.3422 8.96254C21.5271 11.7522 21.457 14.5545 21.1319 17.3346C20.8953 19.3571 19.271 20.9421 17.2589 21.167C13.7924 21.5544 10.209 21.5544 6.74244 21.167C4.73038 20.9421 3.10605 19.3571 2.8695 17.3346C2.45496 13.7903 2.45496 10.2097 2.8695 6.66543C3.10605 4.6429 4.73038 3.05789 6.74244 2.83301C9.37219 2.5391 12.0691 2.46815 14.7313 2.62016C14.9029 2.62996 14.9811 2.83757 14.8596 2.95909L13.867 3.95165C13.7883 4.03034 13.6804 4.07261 13.5692 4.06885C11.3428 3.99376 9.10122 4.07872 6.90905 4.32373C5.57894 4.47239 4.51346 5.522 4.35934 6.83968C3.95834 10.2682 3.95834 13.7318 4.35934 17.1603C4.51346 18.478 5.57894 19.5276 6.90905 19.6763C10.2649 20.0513 13.7365 20.0513 17.0923 19.6763C18.4224 19.5276 19.4879 18.478 19.642 17.1603Z" fill="#374957"/>
                </svg>
              </button>
              <button class="delete-btn" @click.stop="handleDelete(bundle)">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M11 0V1H16V3H15V16C15 17.1 14.1 18 13 18H3C1.9 18 1 17.1 1 16V3H0V1H5V0H11ZM3 16H13V3H3V16ZM5 5H7V14H5V5ZM11 5H9V14H11V5Z" fill="#D10000"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <Pagination
        class="pb-pagination"
        :total_items="totalCount"
        :current_page="currentPage"
        :per_page="perPage"
        :per_page_options="[5, 10, 15]"
        @update:current_page="handlePageChange"
        @update:per_page="handlePageSizeChange"
      />
    </div>

    <!-- Archive Confirmation Dialog -->
    <Dialog
      v-model="showDeleteDialog"
      title="Archive Bundle"
      dialog-type="error"
      dialog-size="small"
      :close-on-overlay="true"
      @close="closeDeleteDialog"
    >
      <template #header-icon>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="3 6 5 6 21 6"></polyline>
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
          <path d="M10 11v6"></path>
          <path d="M14 11v6"></path>
          <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
        </svg>
      </template>

      <div class="archive-bundle-body">
        <p class="archive-bundle-message">
          Are you sure you want to archive <strong>{{ bundleToDelete?.name }}</strong>? You can restore it later from archived products.
        </p>
      </div>

      <template #footer>
        <ResponsiveButton
          button_type="outlined"
          built_in_theme="error"
          size="medium"
          display_name="Cancel"
          @click-button="closeDeleteDialog"
        />
        <ResponsiveButton
          button_type="filled"
          built_in_theme="error"
          size="medium"
          display_name="Archive"
          @click-button="confirmDelete"
        />
      </template>
    </Dialog>

    <ShortcutDialog
      :is-show-dialog="showCreateDialog"
      :selected-shortcut="mappedEditingBundle"
      :selected-provider="selectedProvider"
      :hide-delete-button="true"
      @close="closeCreateDialog"
    />
    </div> <!-- End products-content-wrapper -->
    </Loading>
  </div>
</template>

<script>
import ShortcutDialog from '@/views/va-orderTest/order-page/favorites/favorite-shortcuts/components/ShortcutDialog.vue';
import { notify } from '@/utils/notify';
import { getShortcutsApi, deleteShortcutApi } from '@/api/va-order-page/pricing/item/catalog';
import { getBestDealParams, getBestDealSuggestion } from '@/utils/va-order-test/best-deal-extract/index.js';
import { getCustomPanelList } from '@/utils/va-order-test/best-deal-extract/helpers.js';
import { markRaw } from 'vue';
import { getMarkUps } from '@/api/markup';
import Loading from '@/NewLibrary/Loading/Loading.vue';
import { handlePatientPurchaseEligibility } from '@/views/va-orderTest/order-page/patientPurchaseEligibility.js';

export default {
  name: 'ProductBundles',
  components: {
    ShortcutDialog,
    Loading,
  },

  props: {
    currencySymbol: {
      type: String,
      default: '$',
    },
    providerOptions: {
      type: Array,
      default: () => [],
    },
    searchQuery: {
      type: String,
      default: '',
    },
    filterProvider: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      bundlesData: [],
      loading: false,
      rawShortcuts: [], // Store raw API data
      sortBy: 'default',
      selectedProvider: '',
      perPage: 10,
      currentPage: 1,

      sortByOptions: [
        // { label: 'Bundle Name A-Z', value: 'nameAZ' },
        // { label: 'Bundle Name Z-A', value: 'nameZA' },
        { label: 'Recommendation', value: 'default' },
        { label: 'Name A-Z', value: 'nameAZ' },
        { label: 'Name Z-A', value: 'nameZA' },
        { label: 'Price Highest to Lowest', value: 'price_descending' },
        { label: 'Price Lowest to Highest', value: 'price_ascending' },
      ],

      // Best deal calculation support
      bestDealSupportObj: null,    // support object for best deal
      discountMap: null,            // discount mapping
      dbsMappingData: null,         // DBS mapping data
      bestDealParamsReady: false,   // flag indicating if best deal params are loaded

      // Markup schemes cache (keyed by account_id)
      markupSchemes: {},            // Cached markup schemes from getMarkUps API

      // Delete confirmation dialog
      showDeleteDialog: false,
      bundleToDelete: null,

      // Create/Edit bundle dialog
      showCreateDialog: false,
      editingBundle: null,
    };
  },

  computed: {
    filteredItems() {
      let filtered = [...this.bundlesData];

      // Step 1: Filter by search query
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        const query = this.searchQuery.trim().toLowerCase();
        filtered = filtered.filter(item =>
          item.name.toLowerCase().includes(query)
        );
      }

      // Step 2: Sort
      if (this.sortBy === 'nameAZ') {
        filtered.sort((a, b) => a.name.localeCompare(b.name));
      } else if (this.sortBy === 'nameZA') {
        filtered.sort((a, b) => b.name.localeCompare(a.name));
      } else if (this.sortBy === 'price_descending') {
        filtered.sort((a, b) => b.markedUpPrice - a.markedUpPrice);
      } else if (this.sortBy === 'price_ascending') {
        filtered.sort((a, b) => a.markedUpPrice - b.markedUpPrice);
      }

      return filtered;
    },

    displayItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredItems.slice(start, end);
    },

    totalCount() {
      return this.filteredItems.length;
    },

    allSelected() {
      const currentDisplay = this.displayItems;
      return currentDisplay.length > 0 &&
        currentDisplay.every(item => item.selected);
    },

    selectedCount() {
      return this.bundlesData.filter(bundle => bundle.selected).length;
    },

    selectedItems() {
      return this.bundlesData.filter(bundle => bundle.selected);
    },

    // Maps the local editingBundle format to the shape expected by ShortcutDialog
    mappedEditingBundle() {
      if (!this.editingBundle) return null;
      const raw = this.editingBundle._raw;
      if (!raw) return null;

      // Get markup percentage from markupSchemes
      let markupPercentage = null;
      const accountSchemes = this.markupSchemes[this.selectedProvider] || [];
      const markupScheme = accountSchemes.find(s =>
        s.item_type === 'shortcutId' && String(s.item_id) === String(raw.shortcut_id)
      );
      if (markupScheme && markupScheme.markup_type === 'added_percentage') {
        // Convert decimal to percentage (e.g., 0.1 -> 10)
        markupPercentage = String((markupScheme.markup_value * 100).toFixed(0));
      }

      return {
        id: raw.shortcut_id,
        shortcutName: raw.shortcut_name,
        isSelect: false,
        // Include customer_id for markup lookup (affiliate_id)
        customer_id: this.selectedProvider,
        // Include markup percentage for pre-filling
        markupPercentage,
        testIdList: (raw.tests || []).map(t => {
          const parentId = t.meta_data?.parent_id;
          return parentId ? `${t.test_id}_${parentId}` : t.test_id;
        }),
        discountPanelIdList: (raw.bundles || [])
          .filter(b => b.bundle_type === 'discountpanel')
          .map(b => b.bundle_id),
        customPanelIdList: (raw.bundles || [])
          .filter(b => b.bundle_type === 'custompanel')
          .map(b => b.bundle_id),
        xChooseYIdList: (raw.bundles || [])
          .filter(b => b.bundle_type === 'xchoosey')
          .map(b => ({
            bundleId: b.bundle_id,
            selectedZoomerIdList: b.meta_data?.selected_test_ids || [],
            selectedSupplementIdList: b.meta_data?.supplement_test_ids || [],
            zoomerList: [],
            customPanelId: null,
          })),
      };
    },

    // Get user info from store for API call
    // Use VA Portal user info for correct customer_id and clinic_id (same as VA Portal uses)
    userInfo() {
      return this.$store?.state?.orderTest?.user?.userInfo || this.$store?.state?.login?.userInfo;
    },

    // Check if user is clinic role (EHR uses user_roles array)
    isClinic() {
      const userRoles = this.userInfo?.user_roles || [];
      return Array.isArray(userRoles) && userRoles.includes('clinic');
    },
  },

  watch: {
    providerOptions: {
      handler(newOpts) {
        if (newOpts.length > 0 && !this.selectedProvider) {
          // Use filterProvider (from parent's default_provider) if available, otherwise use first option
          if (this.filterProvider && newOpts.some(p => p.value === this.filterProvider)) {
            this.selectedProvider = this.filterProvider;
          } else {
            this.selectedProvider = newOpts[0].value;
          }
        }
      },
      immediate: true,
    },
    // Also watch filterProvider to update selectedProvider when parent changes it
    filterProvider: {
      handler(newVal) {
        if (newVal && this.providerOptions.some(p => p.value === newVal)) {
          this.selectedProvider = newVal;
        }
      },
      immediate: true,
    },
    sortBy() {
      this.currentPage = 1;
    },
    // Re-fetch data when provider changes
    selectedProvider: {
      async handler(newVal, oldVal) {
        this.currentPage = 1;
        if (newVal && newVal !== oldVal) {
          console.log('[ProductBundles] selectedProvider changed to', newVal, '- re-fetching data');
          // Re-fetch markup schemes, custom panels, and shortcuts for the new provider
          await this.fetchMarkupSchemes();
          await this.initBestDealParams();
          await this.fetchShortcuts();
        }
      },
      immediate: false,
    },
    searchQuery() {
      this.currentPage = 1;
    },
    userInfo: {
      handler(newVal, oldVal) {
        // Only fetch if userInfo changed after initial mount (e.g., provider switch)
        // Initial fetch is handled in mounted() after setting userInfo
        if (oldVal && newVal && newVal.customer_id && newVal.clinic_id) {
          this.fetchShortcuts();
        }
      },
      immediate: false,
    },
  },

  async mounted() {
    // First, ensure VA Portal user info is loaded from EHR's login store
    // (EHR stores userInfo in login module, not sessionStorage like VA Portal)
    try {
      const ehrUserInfo = this.$store.state?.login?.userInfo;
      if (ehrUserInfo) {
        // Map EHR userInfo to VA Portal format and set it in orderTest/user store
        const vaUserInfo = {
          ...ehrUserInfo,
          isClinic: Array.isArray(ehrUserInfo.user_roles) && ehrUserInfo.user_roles.includes('clinic'),
        };
        this.$store.commit('orderTest/user/SET_USER_INFO', vaUserInfo);
        console.log('[ProductBundles] VA Portal userInfo set from EHR:', vaUserInfo);
      } else {
        console.warn('[ProductBundles] No EHR userInfo available');
      }
    } catch (error) {
      console.error('[ProductBundles] Failed to set VA Portal userInfo:', error);
    }

    // Handle VA panel visibility (hide_va beta program)
    // eZ-Bill has no patient context, so this only checks the clinic's hide_va flag
    const vaUserInfo = this.$store.state.orderTest.user.userInfo;
    await handlePatientPurchaseEligibility([], vaUserInfo);

    // Initialize data required for best deal
    await this.initBestDealParams();

    // No need to call fetchMarkupSchemes/fetchShortcuts here —
    // the selectedProvider watcher (triggered by providerOptions watcher setting selectedProvider)
    // already handles fetching all data when provider is set.
  },

  methods: {
    handleCreateBundle() {
      this.editingBundle = null;
      this.showCreateDialog = true;
    },
    async closeCreateDialog() {
      this.showCreateDialog = false;
      this.editingBundle = null;
      // Reload markup schemes first (in case markup was updated during create/edit)
      await this.fetchMarkupSchemes();
      // Then reload shortcuts data after create/edit
      await this.fetchShortcuts();
    },
    async handleBundleSaved(savedData) {
      // Refresh the shortcuts list after save
      await this.fetchShortcuts();
      // Emit event to parent
      this.$emit('bundle-saved', savedData);
    },
    handleBundleDelete(bundle) {
      // Set the bundle to delete and show delete dialog
      this.bundleToDelete = bundle;
      this.showDeleteDialog = true;
    },
    async initBestDealParams() {
      try {
        const { supportObj, discountMap, dbsMappingData } = await getBestDealParams();

        // IMPORTANT: Mark as non-reactive IMMEDIATELY to prevent Vue from traversing circular refs
        // The tree nodes have parent/descendants that reference each other, causing infinite recursion
        const rawSupportObj = markRaw(supportObj);
        const rawDiscountMap = markRaw(discountMap);
        const rawDbsMappingData = markRaw(dbsMappingData);

        // Fetch custom panels separately and merge into bundleList
        const customPanelList = await this.fetchCustomPanels();

        // Merge custom panels into bundleList
        rawSupportObj.bundleList = [...(rawSupportObj.bundleList || []), ...customPanelList];

        // Also update Vuex store so ShortcutDialog can access the custom panels
        // Use commit directly because setCustomPanelList action doesn't accept parameters
        this.$store.commit('orderTest/panels/SET_CUSTOM_PANEL_LIST', customPanelList);

        this.bestDealSupportObj = rawSupportObj;
        this.discountMap = rawDiscountMap;
        this.dbsMappingData = rawDbsMappingData;
        this.bestDealParamsReady = true;
      } catch (error) {
        console.error('Error initializing best deal params:', error);
        this.bestDealParamsReady = false;
      }
    },
    async fetchMarkupSchemes() {
      // Fetch markup schemes for current account (clinic or customer)
      const userInfo = this.userInfo;
      if (!userInfo || (!userInfo.customer_id && !userInfo.clinic_id)) {
        console.warn('[ProductBundles] No user info available for fetching markup schemes');
        return;
      }

      const isClinic = this.isClinic;
      const { customer_id, clinic_id } = userInfo;
      const accountId = isClinic ? clinic_id : customer_id;

      // Build affiliate_ids same as ez-bill/index.vue
      let affiliateIds;
      if (isClinic && this.$parent?.providerOptions?.length > 0) {
        affiliateIds = this.$parent.providerOptions.map(p => Number(p.value));
      } else {
        affiliateIds = [Number(customer_id)];
      }

      try {
        const { data } = await getMarkUps({
          account_type: isClinic ? 'clinic' : 'customer',
          account_id: Number(accountId),
          affiliate_ids: affiliateIds,
          customer_id: Number(customer_id),
          clinic_id: Number(clinic_id)
        });

        // API returns markup_schemes as { affiliateId: [...schemes] }
        // Store the entire object for lookup by affiliate_id
        const markupSchemesData = data?.markup_schemes || {};
        this.markupSchemes = markupSchemesData;
      } catch (error) {
        console.error('[ProductBundles] Error fetching markup schemes:', error);
        this.markupSchemes = {};
      }
    },
    async fetchShortcuts() {
      const userInfo = this.userInfo;
      if (!userInfo || !userInfo.customer_id || !userInfo.clinic_id) {
        console.warn('No user info available for fetching shortcuts');
        return;
      }

      this.loading = true;
      try {
        const { customer_id, clinic_id } = userInfo;
        const isClinic = this.isClinic;
        // For clinic users, use selectedProvider (same as VA Portal uses selectedAffId)
        const customerIdForApi = isClinic ? this.selectedProvider : customer_id;
        if (!customerIdForApi) {
          console.warn('[ProductBundles] No valid customer_id for fetching shortcuts, skipping');
          return;
        }
        const params = {
          clinic_id,
          customer_id: customerIdForApi,
          account_type: isClinic ? "clinic" : "customer"
        };

        const { data } = await getShortcutsApi(params);
        this.rawShortcuts = data || [];
        await this.convertShortcutsToBundles();
      } catch (error) {
        console.error('Error fetching shortcuts:', error);
        notify({
          type: 'error',
          message: 'Failed to load shortcuts. Please try again.'
        });
      } finally {
        this.loading = false;
      }
    },

    // Fetch custom panels (Special Price Custom Panels)
    async fetchCustomPanels() {
      const userInfo = this.userInfo;
      if (!userInfo || !userInfo.customer_id || !userInfo.clinic_id) {
        console.warn('[ProductBundles] No user info available for fetching custom panels');
        return [];
      }

      try {
        const { customer_id, clinic_id } = userInfo;
        const isClinic = this.isClinic;
        // For clinic users, use selectedProvider (same as VA Portal uses selectedAffId)
        // VA Portal logic: customerId = role === "customer" ? userInfo.customer_id : selectedAffId
        const customerIdForApi = isClinic ? this.selectedProvider : customer_id;
        const payload = {
          currency: 'usd',
          clinicId: clinic_id,
          customerId: customerIdForApi,
          accountType: isClinic ? 'clinic' : 'customer'
        };

        console.log('[ProductBundles] Fetching custom panels with payload:', payload);
        console.log('[ProductBundles] Current userInfo:', {
          customer_id: userInfo.customer_id,
          clinic_id: userInfo.clinic_id,
          isClinic: isClinic,
          user_roles: userInfo.user_roles,
          selectedProvider: this.selectedProvider
        });

        const customPanelList = await getCustomPanelList(payload);
        console.log('[ProductBundles] Fetched custom panels:', customPanelList.length);
        console.log('[ProductBundles] Custom panel details:', customPanelList.map(p => ({
          bundleId: p.bundleId,
          bundleName: p.bundleName,
          category: p.category,
          price: p.price
        })));
        return customPanelList || [];
      } catch (error) {
        console.error('Error fetching custom panels:', error);
        // Don't show notification for custom panels error - they're optional
        return [];
      }
    },

    // Convert shortcut API data to bundle display format with best deal pricing
    async convertShortcutsToBundles() {
      // Wait if best deal params are not ready yet
      if (!this.bestDealSupportObj || !this.discountMap) {
        await this.initBestDealParams();
      }

      const { testNodeList, bundleList } = this.bestDealSupportObj;

      this.bundlesData = await Promise.all(this.rawShortcuts.map(async (shortcut) => {
        // 1. Build test_id_list (handle parent_id and xChooseY)
        let test_id_list = [];

        // Handle tests
        shortcut.tests?.forEach(test => {
          const parentId = test.meta_data?.parent_id;
          const testId = parentId ? `${test.test_id}_${parentId}` : test.test_id;
          test_id_list.push(testId);
        });

        // Handle xChooseY in bundles
        shortcut.bundles?.forEach(bundle => {
          if (bundle.bundle_type === 'xchoosey') {
            const { selected_test_ids = [], supplement_test_ids = [] } = bundle.meta_data || {};
            test_id_list.push(...selected_test_ids, ...supplement_test_ids);
          }
          // discountpanel and custompanel use bundle_id directly
          else if (bundle.bundle_type === 'discountpanel' || bundle.bundle_type === 'custompanel') {
            // These will be passed through discount_panel_id_list
          }
        });

        // 2. Build discount_panel_id_list and custom_panel_id_list separately
        // VA Portal: custom panels don't participate in best deal calculation, priced separately
        let discount_panel_id_list = (shortcut.bundles || [])
          .filter(b => b.bundle_type === 'discountpanel')
          .map(b => b.bundle_id);

        let custom_panel_id_list = (shortcut.bundles || [])
          .filter(b => b.bundle_type === 'custompanel')
          .map(b => b.bundle_id);

        // 2.1 Calculate custom panels total price (separate calculation, not in best deal)
        let customPanelsPrice = 0;
        custom_panel_id_list.forEach(panelId => {
          const panel = bundleList.find(b => b.bundleId == panelId);
          if (panel?.price) {
            customPanelsPrice += panel.price;
          }
        });

        // 3. Calculate wholesale price using best deal (same as VA Portal)
        // In VA Portal: wholeSalePrice = best_deal_price + custom_panels_price
        let wholeSalePrice = 0;
        let markedUpPrice = 0;

        if (this.bestDealParamsReady && (test_id_list.length > 0 || discount_panel_id_list.length > 0)) {
          try {
            const payload = {
              test_id_list: test_id_list.map(id => String(id)),
              discount_panel_id_list: discount_panel_id_list.map(id => String(id)),
              is_dbs: shortcut.is_dbs || false,
              is_new_vw_beta: true
            };

            const result = getBestDealSuggestion(
              payload,
              this.bestDealSupportObj,
              this.discountMap,
              this.dbsMappingData
            );
            // VA Portal: wholeSalePrice = best_deal_price + custom_panels_price
            wholeSalePrice = Number(result.best_deal_price || 0) + customPanelsPrice;
            // Default markedUpPrice = wholeSalePrice (no markup)
            markedUpPrice = wholeSalePrice;

            // 4. APPLY MARKUP SCHEME to get retail price (if exists)
            // markupSchemes is keyed by affiliate_id (customer_id for the provider)
            // For clinic: use selected provider's customer_id
            // For customer: use customer_id
            const isClinic = this.isClinic;
            const affiliateId = isClinic 
              ? (this.selectedProvider || this.userInfo.customer_id)
              : this.userInfo.customer_id;
            const accountSchemes = this.markupSchemes[affiliateId] || [];

            // Find markup scheme for this shortcut (item_type: 'shortcutId')
            // Note: item_id from API might be string or number, shortcut_id might also vary
            const markupScheme = accountSchemes.find(s =>
              s.item_type === 'shortcutId' && String(s.item_id) === String(shortcut.shortcut_id)
            );

            if (markupScheme && markupScheme.markup_type !== 'no_markup') {
              // Calculate retail price based on markup type (same as VA Portal helpers.js)
              const { markup_type, markup_value } = markupScheme;
              if (markup_type === 'added_percentage') {
                markedUpPrice = wholeSalePrice * (1 + markup_value);
              } else if (markup_type === 'fixed_profit') {
                markedUpPrice = wholeSalePrice + markup_value;
              } else if (markup_type === 'fixed_price') {
                markedUpPrice = markup_value;
              }
            }
          } catch (error) {
            console.error('Error calculating best deal for shortcut:', shortcut.shortcut_name, error);
            // Fallback: calculate raw price from tests
            test_id_list.forEach(testId => {
              const testNode = testNodeList.find(node =>
                node.id === testId || node.id == testId || node.value?.originalId == testId
              );
              if (testNode?.value?.price) {
                wholeSalePrice += testNode.value.price;
              }
            });
            wholeSalePrice += customPanelsPrice;
            markedUpPrice = wholeSalePrice;
          }
        } else {
          // Fallback if best deal not ready: calculate raw price
          test_id_list.forEach(testId => {
            const testNode = testNodeList.find(node =>
              node.id === testId || node.id == testId || node.value?.originalId == testId
            );
            if (testNode?.value?.price) {
              wholeSalePrice += testNode.value.price;
            }
          });
          discount_panel_id_list.forEach(panelId => {
            const panel = bundleList.find(b => b.bundleId == panelId);
            if (panel?.price) {
              wholeSalePrice += panel.price;
            }
          });
          wholeSalePrice += customPanelsPrice;
          markedUpPrice = wholeSalePrice;
        }

        // Count total items in the shortcut
        const testCount = shortcut.tests?.length || 0;
        const bundleCount = shortcut.bundles?.length || 0;
        const totalItems = testCount + bundleCount;

        return {
          id: shortcut.shortcut_id,
          bundleId: shortcut.shortcut_id,
          type: 'shortcut',
          name: shortcut.shortcut_name,
          category: 'Shortcut',
          wholeSalePrice: wholeSalePrice,
          price: wholeSalePrice,
          markedUpPrice: markedUpPrice,
          savings: wholeSalePrice - markedUpPrice,
          selected: false,
          testCount: totalItems,
          // Store original data for reference
          _raw: shortcut,
        };
      }));
    },

    getTestDescription(bundle) {
      const raw = bundle._raw;
      if (!raw) {
        return `${bundle.testCount} items`;
      }

      // Build description from tests (display_name) and bundles (bundle_name)
      const testNames = raw.tests?.map(t => t.display_name || t.test_name || t.name) || [];
      const bundleNames = raw.bundles?.map(b => b.bundle_name || b.name) || [];

      const allNames = [...testNames, ...bundleNames];
      if (allNames.length > 0) {
        const displayNames = allNames.slice(0, 3);
        let result = displayNames.join(', ');
        if (allNames.length > 3) {
          result += ` +${allNames.length - 3} more`;
        }
        return result;
      }

      return `${bundle.testCount} items`;
    },

    toggleSelection(id, status) {
      const bundle = this.bundlesData.find(b => b.id === id);
      if (bundle) {
        if (status !== undefined) {
          bundle.selected = status === 'checked';
        } else {
          bundle.selected = !bundle.selected;
        }
      }
    },

    toggleSelectAll(status) {
      const newState = status === 'checked';
      this.displayItems.forEach(bundle => {
        bundle.selected = newState;
      });
    },

    handleEdit(bundle) {
      this.editingBundle = bundle;
      this.showCreateDialog = true;
    },

    handleDelete(bundle) {
      // Show confirmation dialog instead of deleting directly
      this.bundleToDelete = bundle;
      this.showDeleteDialog = true;
    },

    closeDeleteDialog() {
      this.showDeleteDialog = false;
      this.bundleToDelete = null;
    },

    async confirmDelete() {
      if (!this.bundleToDelete) return;

      try {
        const bundle = this.bundleToDelete;
        const userInfo = this.userInfo;
        const isClinic = this.isClinic;
        // For clinic users, use selectedProvider (same as VA Portal uses selectedAffId)
        const customerIdForApi = isClinic ? this.selectedProvider : userInfo.customer_id;
        const params = {
          clinic_id: userInfo.clinic_id,
          customer_id: customerIdForApi,
          account_type: isClinic ? "clinic" : "customer",
          shortcut_id: bundle.id,
        };

        await deleteShortcutApi(params);

        notify({
          type: 'success',
          message: `Shortcut "${bundle.name}" has been archived.`
        });

        // Reload shortcuts data after delete
        await this.fetchShortcuts();

        // Notify parent to refresh
        this.$emit('bundle-deleted', bundle);
      } catch (error) {
        console.error('Error deleting shortcut:', error);
        notify({
          type: 'error',
          message: 'Failed to archive shortcut. Please try again.'
        });
      } finally {
        this.closeDeleteDialog();
      }
    },

    handlePageChange(page) {
      this.currentPage = page;
    },

    handlePageSizeChange(size) {
      this.perPage = size;
      this.currentPage = 1;
    },
  },
};
</script>

<style scoped>
.product-bundles-content {
  position: relative;
  padding: 20px 0;
  min-height: 500px;
}

.products-content-wrapper.loading {
  opacity: 0.3;
  pointer-events: none;
}

/* Filter Row - Vibrant Products Style */
.filter-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 250px;
}

.filter-label {
  overflow: hidden;
  color: var(--color-Grey-Grey-4A, #4A4A4A);
  text-overflow: ellipsis;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
}

/* Push the Create New Bundle button to the right of the filter row */
.add-new-bundle-btn-wrapper {
  margin-left: auto;
}

/* Breathing space between the table and the Pagination */
.pb-pagination {
  margin-top: 16px;
}

/* Products Table */
.products-table-wrapper {
  display: flex;
  flex-direction: column;
}

.products-table {
  display: flex;
  flex-direction: column;
  border: none;
  overflow: hidden;
}

/* Table Header - VP Style */
.table-header {
  display: flex;
  background-color: #F9FAFB;
  border-bottom: 1px solid #E5E7EB;
}

.table-header .th {
  padding: 8px 12px;
  color: var(--IconGrey, #374957);
  text-align: center;
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  display: flex;
  align-items: center;
}

.table-header .th.checkbox {
  width: 50px;
  flex-shrink: 0;
  padding-left: 12px;
  padding-right: 12px;
  justify-content: center;
}

.table-header .th.bundle-name {
  flex: 2.5;
  min-width: 0;
  max-width: 400px;
  text-align: left;
  overflow: hidden;
}

.table-header .th.price,
.table-header .th.actions {
  flex: 1;
  text-align: center;
  min-width: 100px;
  justify-content: center;
}

/* Table Body */
.table-body {
  min-height: 300px;
  max-height: 500px;
  overflow-y: auto;
}

.table-body::-webkit-scrollbar {
  width: 6px;
}

.table-body::-webkit-scrollbar-track {
  background: transparent;
}

.table-body::-webkit-scrollbar-thumb {
  background: #C4C4C4;
  border-radius: 3px;
}

.table-body::-webkit-scrollbar-thumb:hover {
  background: #999;
}

/* Table Row - VP Style */
.table-row {
  display: flex;
  border-bottom: 1px solid #E5E7EB;
  transition: background-color 0.2s ease;
}

.table-row.clickable:hover {
  background-color: #F9FAFB;
  cursor: pointer;
}

.table-row.clickable {
  cursor: pointer;
}

.table-row .td {
  padding: 8px 12px;
  font-size: 16px;
  color: var(--TrueBlack, #333);
  display: flex;
  align-items: center;
}

.table-row .td.checkbox {
  width: 50px;
  flex-shrink: 0;
  padding-left: 12px;
  padding-right: 12px;
  justify-content: center;
}

.table-row .td.bundle-name {
  flex: 2.5;
  min-width: 0;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  overflow: hidden;
}

.table-row .td.price,
.table-row .td.actions {
  flex: 1;
  text-align: center;
  min-width: 100px;
  justify-content: center;
}

.table-row .td.actions {
  gap: 16px;
}

/* Bundle Info - VP Style */
.bundle-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.bundle-name-text {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #111827;
}

.test-count {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* Action Buttons - VP Style */
.table-row .td.actions .edit-btn,
.table-row .td.actions .delete-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  margin-left: 15px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.table-row .td.actions .edit-btn:hover {
  background-color: rgba(55, 73, 87, 0.1);
}

.table-row .td.actions .delete-btn:hover {
  background-color: #FEEBEB;
}

/* No Data State - VP Style */
.no-data-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  min-height: 250px;
  color: #999;
  font-size: 16px;
  text-align: center;
}

.no-data-state p {
  margin: 0;
  line-height: 1.5;
}
</style>

<style scoped>
.archive-bundle-body {
  display: flex;
  flex-direction: column;
  padding: 12px 0;
}

.archive-bundle-message {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #333;
  line-height: 20px;
  margin: 0;
}
</style>
